import { createSelector } from 'reselect'

import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { initialOfferPageRooms } from 'contexts/OfferPage/offerPageStateReducer'
import { min } from 'lib/array/arrayUtils'
import buildPropertyCalendarPricesKey from 'lib/offer/buildPropertyCalendarPricesKey'
import type { OfferDurationOption } from 'lib/offer/offerDurationTypes'
import { buildPropertyDurationOptions } from 'lib/offer/propertyDurationUtils'
import { isUnavailableRoomRates } from 'lib/offer/propertyTypes'

import {
  getPropertyCheapestPromotionRates,
  getPropertyRatesByRoom,
  getPropertyRatesKey,
} from './offerPageSelectors'

/* ************ */
/* Status flags */
/* ************ */

/**
 * Returns whether we received no rates for the selected dates and occupancies. Ignores client-side
 * filters.
 */
export const isSoldOutForDatesAndOccupancies = createSelector(
  (state: OfferPageState, appState: App.State) => getPropertyRatesKey(state, appState),
  (_state: OfferPageState, appState: App.State) => appState.offer.propertyRates,
  (combinedKey, rawRoomRatesByKey): boolean => {
    const rawRoomsRates = rawRoomRatesByKey[combinedKey]?.rooms
    if (!rawRoomsRates) return false
    return Object.values(rawRoomsRates).every((rawRoomRates) => {
      if (isUnavailableRoomRates(rawRoomRates)) return true
      return !(rawRoomRates.lists.hotelOnly.length || rawRoomRates.lists.hotelWithFlights.length)
    })
  },
)

/* *********************** */
/* Cheapest promotion rate */
/* *********************** */

export const hasCheapestPromotionRate = createSelector(
  (state: OfferPageState, appState: App.State) => getPropertyCheapestPromotionRates(state, appState),
  ([cheapestHotelOnlyPromotionRate, cheapestHotelWithFlightsPromotionRate]): boolean => !!(cheapestHotelOnlyPromotionRate || cheapestHotelWithFlightsPromotionRate),
)

/* ************************* */
/* Cheapest recommended rate */
/* ************************* */

type CheapestRecommendedRoomAndRate = [App.Property.Room, App.EnrichedPropertyRate] | undefined
export type CheapestRecommendedRoomAndRates = [
  CheapestRecommendedRoomAndRate,
  CheapestRecommendedRoomAndRate,
]

const EMPTY_CHEAPEST_RECOMMENDED_ROOM_AND_RATES: CheapestRecommendedRoomAndRates = [undefined, undefined]

export const getCheapestRecommendedRoomAndRates = createSelector(
  (state: OfferPageState) => state.property?.rooms,
  (state: OfferPageState, appState: App.State) => getPropertyRatesByRoom(state, appState),
  (rooms, ratesByRoom): CheapestRecommendedRoomAndRates => {
    if (!(rooms && ratesByRoom.length)) return EMPTY_CHEAPEST_RECOMMENDED_ROOM_AND_RATES
    // Only check rates that are directly listed. Rates that can only be accessed indirectly (such
    // as through a modal) aren't discoverable enough.
    const hotelOnlyRecommendedRates = ratesByRoom.flatMap(room => 'orderedHotelOnlyRates' in room
      ? room.orderedHotelOnlyRates.filter(rate => rate.calloutReason === 'popular-occupancy')
      : [],
    )
    const hotelWithFlightsRecommendedRates = ratesByRoom.flatMap(room => 'orderedHotelWithFlightsRates' in room
      ? room.orderedHotelWithFlightsRates.filter(rate => rate.calloutReason === 'popular-occupancy' && typeof rate.cheapestFlightPrice === 'number')
      : [],
    )
    const cheapestHotelOnlyRecommendedRate = min(hotelOnlyRecommendedRates, rate => rate.luxPlusTotals?.displayPrice ?? rate.totals.displayPrice)
    const cheapestHotelWithFlightsRecommendedRate = min(hotelWithFlightsRecommendedRates, rate => rate.luxPlusTotals?.displayPrice ?? rate.totals.displayPrice)
    const cheapestHotelOnlyRecommendedRoomAndRate: CheapestRecommendedRoomAndRate = cheapestHotelOnlyRecommendedRate && rooms[cheapestHotelOnlyRecommendedRate.roomId]
      ? [rooms[cheapestHotelOnlyRecommendedRate.roomId], cheapestHotelOnlyRecommendedRate]
      : undefined
    const cheapestHotelWithFlightsRecommendedRoomAndRate: CheapestRecommendedRoomAndRate = cheapestHotelWithFlightsRecommendedRate && rooms[cheapestHotelWithFlightsRecommendedRate.roomId]
      ? [rooms[cheapestHotelWithFlightsRecommendedRate.roomId], cheapestHotelWithFlightsRecommendedRate]
      : undefined
    return [
      cheapestHotelOnlyRecommendedRoomAndRate,
      cheapestHotelWithFlightsRecommendedRoomAndRate,
    ]
  },
)

/* ***************************** */
/* Calendar and duration options */
/* ***************************** */

export const getCalendarDayPrices = createSelector(
  (state: OfferPageState) => state.property,
  (_state: OfferPageState, appState: App.State) => appState.offer.propertyCalendarPrices,
  (property, calendarPrices): { [isoDate: string]: App.Property.CalendarDay } | undefined => {
    const key = buildPropertyCalendarPricesKey({
      propertyId: property?.id,
      // TODO(HP-2799): Refactor this selector to default to the duration and occupancy rooms
      // selected in `OfferPageState`.
      duration: property?.promotion?.marketedLos,
      occupancies: property?.promotion?.marketedOccupancy ? [property.promotion.marketedOccupancy] : initialOfferPageRooms,
    })
    return calendarPrices[key]?.days
  },
)

export const hasCalendarDayLuxPlusPrices = createSelector(
  (state: OfferPageState, appState: App.State) => getCalendarDayPrices(state, appState),
  (calendarDayPrices): boolean =>
    !!calendarDayPrices && Object.values(calendarDayPrices).some(({ campaignLuxPlusDisplayPrice }) => !!campaignLuxPlusDisplayPrice),
)

export const getDurationOptions = createSelector(
  (state: OfferPageState) => state.property?.durationOptions,
  (state: OfferPageState) => state.checkInDate,
  (durationOptions, checkIn): Array<OfferDurationOption> =>
    buildPropertyDurationOptions(durationOptions, checkIn),
)
